// @file Whiteboard toolbar store, mainly for the visuals of secondary menu and tooltips.
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { getAllTools } from '@@/bits/whiteboard_tools'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import type { WhiteboardSecondaryMenu, WhiteboardTool } from '@@/types/whiteboard'
import { chunk } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// 8 (distance between toolbar icon and toolbar edge) + 4 (distance between toolbar edge and menu)
const GEO_SHAPE_OR_COLOR_MENU_OFFSET = 8 + 4

export const useWhiteboardToolbarStore = defineStore('whiteboardToolbar', () => {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()

  // #region How tools are shown in toolbar
  const isSingleRowLayout = computed(() => {
    return whiteboardStore.isLoadingPortraitMobileLayout
  })

  const isSingleColumnLayout = computed(() => {
    return !isSingleRowLayout.value && surfaceContainerSizeStore.isContainerSmallerThanTabletLandscape
  })

  const isDoubleColumnLayout = computed(() => {
    return !isSingleRowLayout.value && !isSingleColumnLayout.value
  })

  /**
   * The tools to display in the toolbar, grouped by sections.
   * Select tool is always present in the first section.
   * Undo & redo are always the last section.
   * The layout is structured in a 3D array, where the first level is the sections,
   * second level is the rows in the section, and the third level is the tools in the row.
   */
  const toolSections = computed<string[][][]>(() => {
    // If all tools are enabled, use the standard two-column layout (fixed selected tools).
    if (surfacePermissionsStore.canIAdminister || whiteboardToolsStore.areAllToolsEnabled) {
      return [
        // first section
        [['select', 'laser']],

        // second section
        isAppUsing('sandboxStickers')
          ? [
              ['draw-nested', 'text'],
              ['note', 'geo'],
              ['arrow', 'padlet-stickers'],
              ['padlet-image_search', 'padlet-attachment'],
            ]
          : [
              ['draw-nested', 'text'],
              ['note', 'geo'],
              ['arrow', 'padlet-attachment'],
            ],

        // third section
        [['undo', 'redo']],
      ]
    }

    const numEnabledTools = whiteboardToolsStore.allEnabledToolIds.length

    // > 6 tools enabled
    if (numEnabledTools > 6) {
      // If laser tool is enabled, always group it with select tool
      const isLaserToolEnabled = whiteboardToolsStore.isToolEnabled('laser')
      if (isLaserToolEnabled) {
        const remainingTools = whiteboardToolsStore.allEnabledToolIds.filter((id) => id !== 'laser')
        const toolsToShow =
          remainingTools.length > 6 ? [...remainingTools.slice(0, 5), 'padlet-attachment'] : remainingTools
        return [
          [['select', 'laser']], // first section
          chunk(toolsToShow, 2), // second section
          [['undo', 'redo']], // third section
        ]
      }

      // If number of enabled tools is greater than 7, show the "+n" button (`padlet-attachment` tool)
      const remainingTools =
        numEnabledTools > 7
          ? [...whiteboardToolsStore.allEnabledToolIds.slice(0, 6), 'padlet-attachment']
          : whiteboardToolsStore.allEnabledToolIds

      // Keep select tool in its own section if single row layout
      if (isSingleRowLayout.value) {
        return [
          [['select']], // first section
          chunk(remainingTools, 2), // second section
          [['undo', 'redo']], // third section
        ]
      }

      const toolsToShow = ['select', ...remainingTools]
      return [
        chunk(toolsToShow, 2), // first section
        [['undo', 'redo']], // second section
      ]
    }

    // These cases share the same layout
    if ([6, 4, 2].includes(numEnabledTools)) {
      return [
        [['select']], // first section
        chunk(whiteboardToolsStore.allEnabledToolIds, 2), // second section
        [['undo', 'redo']], // third section
      ]
    }

    // These cases share the same layout
    if ([5, 3].includes(numEnabledTools)) {
      // Keep select tool in its own section if single row layout
      if (isSingleRowLayout.value) {
        return [
          [['select']], // first section
          chunk(whiteboardToolsStore.allEnabledToolIds, 2), // second section
          [['undo', 'redo']], // second section
        ]
      }

      const toolsToShow = ['select', ...whiteboardToolsStore.allEnabledToolIds]
      return [
        chunk(toolsToShow, 2), // first section
        [['undo', 'redo']], // second section
      ]
    }

    // 1 tool enabled, only have 2 sections
    if (numEnabledTools === 1) {
      const toolsToShow = ['select', ...whiteboardToolsStore.allEnabledToolIds]
      return [
        chunk(toolsToShow, 2), // first section
        [['undo', 'redo']], // second section
      ]
    }

    // No tools enabled
    return [
      [['select']], // first section
      [
        ['undo'], // second section
        ['redo'],
      ],
    ]
  })

  /**
   * Map the tool IDs to the actual tool objects.
   */
  const displayedToolSections = computed<WhiteboardTool[][][]>(() => {
    const mapToolId = (toolId: string): WhiteboardTool => {
      return getAllTools().find((tool) => tool.id === toolId) as WhiteboardTool
    }

    const mapColumns = (columns: string[]): WhiteboardTool[] => {
      return columns.map(mapToolId).filter((tool): tool is WhiteboardTool => tool !== undefined)
    }

    const mapRows = (rows: string[][]): WhiteboardTool[][] => {
      return rows.map(mapColumns)
    }

    return toolSections.value.map(mapRows)
  })

  const flattenedDisplayedTools = computed<WhiteboardTool[]>(() => {
    return displayedToolSections.value.flat(Infinity) as WhiteboardTool[]
  })
  // #endregion

  // Secondary Menu

  const xSecondaryMenu = ref(true)

  /**
   * If there's only 1 column, all tools are near the screen edge.
   * If there are 2 columns, check if the specified tool ID matches
   * a tool ID in the second column (near the screen edge).
   */
  const isToolInColumnNearScreenEdge = (toolId: string): boolean => {
    return toolSections.value.some((rows) => rows.some((columns) => columns[1] === toolId))
  }

  const secondaryMenuMainAxisOffset = computed(() => {
    if (isToolInColumnNearScreenEdge(whiteboardStore.selectedToolId)) {
      return GEO_SHAPE_OR_COLOR_MENU_OFFSET + 44
    }
    return GEO_SHAPE_OR_COLOR_MENU_OFFSET
  })

  const geoColorMenuOffset = computed(() => {
    return secondaryMenuMainAxisOffset.value + 40 + 4
  })

  const secondaryMenuCrossAxisOffset = computed(() => {
    if (whiteboardStore.selectedToolId === 'draw-nested') {
      const numberOfColorItems = whiteboardStore.selectedSecondaryTool?.secondaryMenu?.items.length ?? 0
      const ICON_HEIGHT = device.touchable ? 36 : 24
      const ICON_MARGIN = 4
      const ICON_LIST_PADDING = 8
      if (numberOfColorItems === 0) {
        return 0
      }
      return (ICON_HEIGHT * numberOfColorItems + ICON_MARGIN * (numberOfColorItems - 1)) / 2 + ICON_LIST_PADDING
    }
    return 0
  })

  // Secondary Menu logic

  function getNestedColorMenu(): WhiteboardSecondaryMenu | null {
    if (whiteboardStore.selectedTool == null) return null
    // Get the current secondary selection ID for the selected tool
    const currentSecondarySelectionId =
      whiteboardStore.toolStates[whiteboardStore.selectedToolId]?.currentSecondarySelectionId

    // Find the corresponding menu item in the secondary menu items
    const selectedItem = whiteboardStore.selectedTool.secondaryMenu?.items.find(
      (item) => item.id === currentSecondarySelectionId,
    )

    // Extract the nested color menu from the selected item, if it exists
    const nestedColorMenu = selectedItem?.secondaryMenu ?? null
    return nestedColorMenu
  }

  function getNestedSelectedColorId(): string | null {
    if (whiteboardStore.selectedTool == null) return null
    // Get the current secondary selection ID for the selected tool
    const currentSecondarySelectionId =
      whiteboardStore.toolStates[whiteboardStore.selectedToolId]?.currentSecondarySelectionId

    // Find the corresponding menu item in the secondary menu items
    const selectedItem = whiteboardStore.selectedTool.secondaryMenu?.items.find(
      (item) => item.id === currentSecondarySelectionId,
    ) as WhiteboardTool
    // Extract the nested color menu from the selected item, if it exists
    const selectedColorId = whiteboardStore.toolStates[selectedItem?.id]?.currentSecondarySelectionId ?? null
    return selectedColorId
  }

  function getFillStyleMenu(): WhiteboardSecondaryMenu | null {
    const fillStyleMenu = {
      id: 'fill-style',
      items: [
        { label: 'None', iconName: 'fill-none', id: 'none', tldrawToolId: 'none' },
        { label: 'White', iconName: 'fill-white', id: 'semi', tldrawToolId: 'semi' },
        { label: 'Solid', iconName: 'fill-solid', id: 'solid', tldrawToolId: 'solid' },
      ],
      defaultSelection: 'semi',
    }
    return fillStyleMenu
  }

  function getFillStyleMenuSelectionId(): string {
    return whiteboardStore.toolStates.geo?.fillStyle ?? 'semi'
  }

  const currentSecondaryMenus = computed<WhiteboardSecondaryMenu[]>(() => {
    // Return an empty array if no tool is selected
    if (whiteboardStore.selectedTool == null) return []

    // Initialize the secondary menus array with the selected tool's secondary menu
    let secondaryMenus = [whiteboardStore.selectedTool.secondaryMenu] as WhiteboardSecondaryMenu[]

    // Add the geoColorMenu if the selected tool is 'geo'
    if (whiteboardStore.selectedTool.id === 'geo' && whiteboardStore.geoColorMenu != null) {
      const fillStyleMenu = getFillStyleMenu()
      if (fillStyleMenu != null) {
        secondaryMenus = secondaryMenus.concat(fillStyleMenu)
      }
      secondaryMenus = secondaryMenus.concat(whiteboardStore.geoColorMenu).reverse()
    }

    if (whiteboardStore.selectedTool?.id === 'draw-nested') {
      const nestedColorMenu = getNestedColorMenu()

      // Add the nested color menu to the secondary menus array
      if (nestedColorMenu != null) {
        secondaryMenus = secondaryMenus.concat(nestedColorMenu)
      }
    }

    if (whiteboardStore.selectedTool?.id === 'arrow' && whiteboardStore.arrowColorMenu != null) {
      secondaryMenus = secondaryMenus.concat(whiteboardStore.arrowColorMenu).reverse()
    }
    // Filter out any null menus from the array
    secondaryMenus = secondaryMenus.filter((menu) => menu != null)

    return secondaryMenus
  })

  const currentSecondarySelectionIds = computed<string[]>(() => {
    let selectionIds = [whiteboardStore.toolStates[whiteboardStore.selectedToolId]?.currentSecondarySelectionId]
    if (whiteboardStore.selectedTool?.id === 'geo' && whiteboardStore.geoColorMenu != null) {
      const fillStyleMenuSelectionId = getFillStyleMenuSelectionId()
      selectionIds = selectionIds.concat(fillStyleMenuSelectionId)
      selectionIds = selectionIds.concat(whiteboardStore.geoColorMenuSelectionId).reverse()
    }
    if (whiteboardStore.selectedTool?.id === 'arrow' && whiteboardStore.arrowColorMenu != null) {
      selectionIds = selectionIds.concat(whiteboardStore.arrowColorMenuSelectionId).reverse()
    }
    if (whiteboardStore.selectedTool?.id === 'draw-nested') {
      const selectedColorId = getNestedSelectedColorId()

      // Add the selected nested color menu to the secondary menus
      if (selectedColorId != null) selectionIds = selectionIds.concat(selectedColorId)
    }

    selectionIds = selectionIds.filter((id) => id != null)
    return selectionIds as string[]
  })

  function handleSecondaryMenuSelection(item, secondaryMenuId): void {
    if (
      whiteboardStore.selectedTool?.id === 'geo' &&
      whiteboardStore.geoColorMenu != null &&
      secondaryMenuId === 'color'
    ) {
      whiteboardStore.geoColorMenuSelectionId = item.id
      return
    }

    if (whiteboardStore.selectedTool?.id === 'geo' && secondaryMenuId === 'fill-style') {
      whiteboardStore.toolStates = {
        ...whiteboardStore.toolStates,
        geo: {
          ...whiteboardStore.toolStates.geo,
          fillStyle: item.id,
        },
      }
      return
    }

    if (
      whiteboardStore.selectedTool?.id === 'arrow' &&
      secondaryMenuId === 'color' &&
      whiteboardStore.arrowColorMenu != null
    ) {
      whiteboardStore.selectArrowColor(item.id)
      return
    }

    if (whiteboardStore.selectedTool?.id === 'draw-nested' && secondaryMenuId === 'color') {
      if (whiteboardStore.selectedSecondaryTool == null) return
      whiteboardStore.toolStates = {
        ...whiteboardStore.toolStates,
        [whiteboardStore.selectedSecondaryTool?.id]: {
          ...whiteboardStore.toolStates[whiteboardStore.selectedSecondaryTool?.id],
          currentSecondarySelectionId: item.id,
        },
      }
      return
    }

    whiteboardStore.toolStates = {
      ...whiteboardStore.toolStates,
      [whiteboardStore.selectedToolId]: {
        ...whiteboardStore.toolStates[whiteboardStore.selectedToolId],
        currentSecondarySelectionId: item.id,
      },
    }
  }
  /** TOOLS' TOOLTIP
   * If the user hover on a tool for 1.5s, we start showing tooltip for any tools hovered on
   * until the user hover out of the toolbar for more than 1s.
   */

  // State
  const xTooltip = ref(false)
  const tooltipToolId = ref<string | null>(null)

  // Utilities
  const TOOLTIPS_ON_DELAY = 1500
  const TOOLTIPS_OFF_DELAY = 1000
  let tooltipShowingTimeout: ReturnType<typeof setTimeout> | null = null
  let tooltipHidingTimeout: ReturnType<typeof setTimeout> | null = null
  function clearTooltipShowingTimeout(): void {
    if (tooltipShowingTimeout != null) {
      clearTimeout(tooltipShowingTimeout)
      tooltipShowingTimeout = null
    }
  }
  function clearTooltipHidingTimeout(): void {
    if (tooltipHidingTimeout != null) {
      clearTimeout(tooltipHidingTimeout)
      tooltipHidingTimeout = null
    }
  }

  // Actions
  function trackToolHoverOn(toolId: string): void {
    tooltipToolId.value = toolId
    clearTooltipShowingTimeout()
    clearTooltipHidingTimeout()
    tooltipShowingTimeout = setTimeout(() => {
      xTooltip.value = true
      clearTooltipShowingTimeout()
    }, TOOLTIPS_ON_DELAY)
  }

  function trackToolHoverOff(): void {
    tooltipToolId.value = null
    clearTooltipShowingTimeout()
    tooltipHidingTimeout = setTimeout(() => {
      xTooltip.value = false
      clearTooltipHidingTimeout()
    }, TOOLTIPS_OFF_DELAY)
  }

  // 8 (distance from tooltip icon to toolbar edge) + 4 (distance from toolbar edge to tooltip)
  const TOOLTIP_OFFSET = 8 + 4

  const tooltipOffset = computed(() => {
    if (tooltipToolId.value == null) return 0
    if (whiteboardStore.isLoadingPortraitMobileLayout) {
      return TOOLTIP_OFFSET
    }
    if (isToolInColumnNearScreenEdge(tooltipToolId.value)) {
      return TOOLTIP_OFFSET + 44
    }
    return TOOLTIP_OFFSET
  })

  return {
    currentSecondaryMenus,
    currentSecondarySelectionIds,
    secondaryMenuMainAxisOffset,
    secondaryMenuCrossAxisOffset,
    geoColorMenuOffset,
    tooltipOffset, // State
    xTooltip,
    tooltipToolId,
    xSecondaryMenu,
    displayedToolSections,
    flattenedDisplayedTools,
    isSingleRowLayout,
    isSingleColumnLayout,
    isDoubleColumnLayout,

    // Actions
    trackToolHoverOn,
    trackToolHoverOff,
    handleSecondaryMenuSelection,
  }
})
